import useAppMethods from '../../hooks/utility/useAppMethods';
import { useLocationData } from './provider/LocationDataProvider';
import { useEffect, useMemo, useState } from 'react';
import moment, { Moment } from 'moment';
import useOrderSlots from '../../hooks/utility/useOrderSlots';
import { useTranslation } from 'react-i18next';
import { CompanyDetailsInterface } from '../../services/exports/Interfaces';

interface DayItem {
  label: string;
  dateLabel: string;
  date: Moment;
  active?: boolean;
}

interface Props {
  company: CompanyDetailsInterface;
}

const useScheduledOrderStuff = ({ company }: Props) => {
  const { t } = useTranslation(null, { keyPrefix: 'Modals:OrderMethod:useScheduledOrderStuff' });
  const { toggleOrderMethodModal } = useAppMethods();
  const { setScheduledFor, orderMethod } = useLocationData();
  const [orderSlot, setOrderSlot] = useState<string>(null);
  const [now] = useState(() => moment());
  const [date, setDate] = useState<Moment>(null);
  const [more, setMore] = useState<boolean>(false);
  const { orderSlots, isSlotsLoading, isHoliday, hasOrderSlotsOnDate, isHolidaysLoading, maxPreOrderDays } =
    useOrderSlots({
      orderMethod,
      startDate: now.format('YYYY-MM'),
      date,
      company,
    });

  const CUSTOM_CALENDAR = {
    sameDay: `[${t('today')}]`,
    nextDay: `[${t('tomorrow')}]`,
    nextWeek: 'ddd',
    lastDay: 'ddd',
    lastWeek: 'ddd',
    sameElse: 'ddd',
  } as const;

  const onBackClick = () => {
    toggleOrderMethodModal({ openModal: true });
  };

  const days = useMemo(() => {
    if (isHolidaysLoading) {
      return [];
    }

    const result: DayItem[] = [];

    for (let i = 0; i <= maxPreOrderDays; i++) {
      const day = now.clone().startOf('day').add(i, 'days');

      if (isHoliday(day.toISOString())) {
        continue;
      }

      const label = day.calendar(CUSTOM_CALENDAR);
      const active = date && day.isSame(date, 'days');

      if (hasOrderSlotsOnDate(day)) {
        result.push({
          label,
          dateLabel: day.format('MMM DD'),
          date: day,
          active,
        });
      }
    }

    if (more) {
      return result;
    }

    if (!date) {
      result.length = 2;

      return result;
    }

    const activeDayIndex = result.findIndex((item) => item.active);

    if (activeDayIndex < 2) {
      result.length = 2;
      return result;
    }

    const startIndex = activeDayIndex % 2 === 0 ? activeDayIndex : activeDayIndex - 1;

    return result.slice(startIndex, startIndex + 2);
  }, [maxPreOrderDays, isHoliday, more, date, isHolidaysLoading, company]);

  const onDayClick = (date: Moment) => {
    setOrderSlot(null);
    setDate(date);
  };
  const onOrderSlotClick = (slot: string) => {
    setOrderSlot(slot);
  };
  const toggleMore = () => setMore((prev) => !prev);
  const onScheduleOrderClick = () => {
    setScheduledFor(orderSlot);
    toggleOrderMethodModal({ openModal: true });
  };
  const hasOrderSlots = date && !!orderSlots?.length;
  const isScheduleOrderDisabled = isSlotsLoading || isHolidaysLoading || !date || !orderSlot;
  const isLoading = isHolidaysLoading || isSlotsLoading;

  useEffect(() => {
    if (!days?.length || date) {
      return;
    }

    setDate(days[0].date);
  }, [days]);

  return {
    onBackClick,
    days,
    orderSlots,
    hasOrderSlots,
    more,
    isScheduleOrderDisabled,
    onDayClick,
    toggleMore,
    isLoading,
    onScheduleOrderClick,
    onOrderSlotClick,
    orderSlot,
  };
};

export default useScheduledOrderStuff;
